import * as Mui from "@mui/material";
import { useContext, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { customTheme } from "../../../../../config/theme/MuiTheme";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { ContextOperation } from "../../../context/OperationContext";
import useOperationTranslation from "../../../hooks/useOperationTranslation";
import useSnackbar from "../../../hooks/useSnackbar";
import { AutoCompleteOption } from "../../common/AutoComplete";
import FormInput from "../../common/FormInput";
import { nameof } from "../../common/ObjectUtils";
import { CreateDamageFormTypes } from "../Types";
import DamageFormService from "../service/DamageFormService";
import TenantForm from "../../../../booking/components/multitenant/TenantForm";
import DealerForm from "../../../../booking/components/multitenant/DealerForm";

const GeneralInfoStep = () => {
    const t = useOperationTranslation();

    const { tenant, setTenant} = useContext(ContextOperation);

    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as CreateDamageFormTypes.ISharedLocationState;
    //const generalInfo = location.state as CreateDamageFormTypes.IGeneralInfo;

    const { globalData } = useContext(GlobalContext);
	const snackbar = useSnackbar();
    
    const mobilityStoreOptions = useMemo<AutoCompleteOption[]>(() => {

        if(!globalData?.user?.mobilityStores) {
            return [];
        }
        const msListOptions : AutoCompleteOption[] = [];
        for (var i = 0; i < globalData?.user?.mobilityStores.length; i++) {

            if (globalData?.user?.mobilityStores[i].id != 0 && tenant === globalData?.user?.mobilityStores[i].tenant) {

                msListOptions.push(
                    {
                        label: globalData?.user?.mobilityStores[i].name,
                        value: globalData?.user?.mobilityStores[i].sapId
                    }
                )
            }
        }
        return msListOptions;
    }, [globalData?.user?.mobilityStores, tenant]);
    

    const form = useForm<CreateDamageFormTypes.IGeneralInfo>({
        defaultValues: {
            plate: locationState?.generalInfo?.plate,
            eventCity: locationState?.generalInfo?.eventCity,
            eventDate: locationState?.generalInfo?.eventDate,
            formManagementSapId: (locationState?.generalInfo?.formManagementSapId ? 
                locationState?.generalInfo?.formManagementSapId : 
                (mobilityStoreOptions.length === 1 ? (mobilityStoreOptions[0].value as string) : undefined))

        }
    });

    const tenantWatch = useWatch({
        name: "tenant",
        control: form.control
    });

    const goBack = () => {
        navigate("/operation/damages/forms");
    }

    const onSubmit = (data: CreateDamageFormTypes.IGeneralInfo) => {
        // go to next page
        DamageFormService.create.get.proposalRa(data.tenant, data.plate, data.eventDate).then(
			response => {
				if (data.eventDate) {

					const state: CreateDamageFormTypes.ISharedLocationState = {
						generalInfo: data,
						rentalAgreement: response
					}
					
					if (!response.raId)
                            snackbar.show("warning", t("damageForm.damageFormCreation.message.noRAFound"));
					
					navigate("/operation/damages/forms/creation/rental-agreement", { state });
				
				} else {

					navigate("/operation/damages/forms/creation/damages", {
						state: {
							generalInfo: data,
						}
					});

				}
			}
		).catch((error) => {
			if (error.response.status === 409) {
				snackbar.show("error", t("damageForm.damageFormCreation.message.multipleRAFound"));
			} else if (error.response.status === 404) {
				snackbar.show("error", t("damageForm.damageFormCreation.message.vehicleNotFound"));
			} else {
				snackbar.show("error", t("damageForm.damageFormCreation.message.retrievalRAError"));

			}
		});
    };

    return(
        <>
            <Mui.Card style={{ margin: "16px"}}>
                <Mui.CardHeader title={t("damageForm.damageFormCreation.generalInfo.titleCard")} />
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Mui.CardContent>
                            <Mui.Grid
                                container
                                alignItems="center"
                                spacing={2.5}
                            >

<Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                    <FormProvider {...form}>
                                        <TenantForm
                                            name={nameof<CreateDamageFormTypes.IGeneralInfo>("tenant")}
                                            multiple={false}
                                            onChange={(e: any) => {
                                                form.setValue("formManagementSapId", undefined);
                                                setTenant(form.getValues("tenant"))
                                                }}

                                        />
                                    </FormProvider>
                                </Mui.Grid>
                                
                                <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                 <FormInput
                                    type="select"
                                    options={mobilityStoreOptions} 
                                    label={t("damageForm.fields.formManagementLms") + " *"}
                                    name={nameof<CreateDamageFormTypes.IGeneralInfo>("formManagementSapId")}
                                    formInstance={form}                           
                                    fullWidth
                                    />
                                    
                                </Mui.Grid>

                                                                
                                <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                    <FormInput 
                                        type="text"
                                        name={nameof<CreateDamageFormTypes.IGeneralInfo>("plate")}
                                        formInstance={form}
                                        fullWidth
                                        label={t("damageForm.damageFormCreation.generalInfo.vehiclePlate") + " *"}
                                    />
                                </Mui.Grid>


                                <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                    <FormInput 
                                        type="text"
                                        name={nameof<CreateDamageFormTypes.IGeneralInfo>("eventCity")}
                                        formInstance={form}
                                        fullWidth
                                        label={t("damageForm.damageFormCreation.generalInfo.accidentCity")}
                                    />
                                </Mui.Grid>

                                <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                    <FormInput 
                                        type="date"
                                        name={nameof<CreateDamageFormTypes.IGeneralInfo>("eventDate")}
                                        formInstance={form}
                                        fullWidth
										maxDateTime={new Date()}
                                        label={t("damageForm.damageFormCreation.generalInfo.accidentDate")}
										validation={{
                                            validate: (val: string) => {
                                                if (val === "invalid") {
                                                    return t("damageForm.damageFormCreation.generalInfo.validations.invalidAccidentDate");
                                                }
                                            }
                                        }}

                                    />
                                </Mui.Grid>


                                {/* There is no dealer mapping in DB */}
                                {/* <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                    <FormProvider {...form}>
                                        <DealerForm
                                            name={nameof<CreateDamageFormTypes.IGeneralInfo>("dealer")}
                                            multiple={false}
                                            tenants={tenantWatch}
                                        />
                                    </FormProvider>
                                </Mui.Grid> */}

                            </Mui.Grid>     
                    </Mui.CardContent>
                    <Mui.CardActions>
                        <Mui.Grid container spacing={2} justifyContent="end">
                            <Mui.Grid item xs={12} md={"auto"}>
                                <Mui.Button 
                                    variant="outlined" 
                                    fullWidth 
                                    size="medium"
                                    onClick={goBack}
                                >
                                    <Mui.Typography color={customTheme.palette.accent.main}>
                                            {t("damageForm.damageFormCreation.button.cancel")}
                                        </Mui.Typography>
                                </Mui.Button>
                            </Mui.Grid>

                            <Mui.Grid item xs={12} md={"auto"}>
                                <Mui.Button 
                                variant="contained"
                                fullWidth 
                                type="submit" 
                                disabled = {(form.watch("plate") && form.watch("tenant") && form.watch("formManagementSapId") ? false : true)}
                                size="medium"
                                >
                                    <Mui.Typography variant="h6" color={'accent'}>
                                    {t("damageForm.damageFormCreation.button.next")}
                                    </Mui.Typography>
                                </Mui.Button>
                            </Mui.Grid>
                        </Mui.Grid>
                    </Mui.CardActions>
                </form>
            </Mui.Card>
        </>
    )
}
export default GeneralInfoStep;